import {
  Box,
  Button,
  Divider,
  List,
  makeStyles,
  Paper,
} from "@material-ui/core";
import React, { useEffect, useMemo, useReducer, useState } from "react";
import { RecipientPotentiallyUnknownOrg } from "../../../../../domain/RecipientsViewModel";
import { Checkbox } from "@ofqual/portal-components/components/Checkbox";
import { SearchDialog } from "@ofqual/portal-components/components/SearchField/SearchDialog";
import { authSelector } from "../../../../../redux/selectors";
import { QueryParams } from "@ofqual/portal-core/core/types";
import { aoService } from "../../../../../services";
import { ManageRecipientOrg } from "./ManageRecipientOrg";
import { useSelector } from "react-redux";
import { reducer } from "./reducer";

const useStyles = makeStyles((theme) => ({
  title: {
    paddingLeft: 5,
  },
  orgs: {
    marginBottom: 25,
    padding: 10,
  },
  body: {
    minHeight: 300,
    maxHeight: "calc(60vh)",
    overflow: "scroll",
  },
  header: {
    padding: 10,
    paddingLeft: 15,
  },
}));

type ManageRecipientsOrgsProps = {
  orgs: RecipientPotentiallyUnknownOrg[];
  onChange: (newOrgs: RecipientPotentiallyUnknownOrg[]) => void;
  haveRoles: boolean;
};

export const ManageRecipientsOrgs: React.FC<ManageRecipientsOrgsProps> = ({
  orgs: defaultOrgs,
  onChange,
  haveRoles,
}) => {
  const classes = useStyles();

  const [dialogOpenProps, setDialogOpenProps] = useState({
    isDialogOpen: false,
    initialSearchText: "",
  });

  //this is a local reducer that handles data for our ManageRecipientsDialog
  const [orgs, dispatch] = useReducer(reducer, defaultOrgs);

  useEffect(() => {
    onChange(orgs);
  }, [orgs, onChange]);

  const { token } = useSelector(authSelector);
  const mappedOrgLookupService = useMemo(
    () => ({
      get: (id: string) => aoService.get(id.toString(), { token }),
      search: (params?: QueryParams) => {
        const extraParams = {
          search: params?.query,
          ofqualRecognisedOnly: true,
          excludeInactive: true,
          filter: "excludePendingRecognition",
        };
        const fullParams = !!params
          ? { ...params, ...extraParams }
          : extraParams;
        return aoService.search(fullParams, { token });
      },
    }),
    [token]
  );

  const isEverythingToDelete =
    !!orgs.length && orgs.every((org) => org.isToDelete);

  const isAnythingToDelete =
    isEverythingToDelete ||
    orgs.some(
      (org) => org.isToDelete || org.users?.some((user) => user.isToDelete)
    );

  const handleCheckAllChange = () =>
    dispatch({
      type: "FLAG_ALL_ORGS",
      payload: { isToDelete: !isEverythingToDelete },
    });

  const handleRemoveSelected = () => dispatch({ type: "REMOVE_SELECTED" });

  const handleSelectOrClose = (val?: { id: string; label: string }) => {
    setDialogOpenProps({ isDialogOpen: false, initialSearchText: "" });
    if (val) {
      dispatch({
        type: "ADD_ORG",
        payload: {
          organisationRecipientId: null,
          id: val.id,
          name: val.label,
          users: [],
          assignedToUpn: null,
          assignedToUpnAO: null,
          assignedToDisplayName: null,
          assignedToDisplayNameAO: null,
          closed: false,
          status: null,
        },
      });
    }
  };

  // this is a great way to read in all the organisations via API and then
  // dispatch the result to our local reducer
  const loadAllOrgs = () => {
    mappedOrgLookupService
      .search({ pageSize: 5000 })
      .then((resp) => {
        const values = resp.data.map((x) => ({
          organisationRecipientId: null,
          id: x.id,
          name: x.label,
          users: [],
          assignedToUpn: null,
          assignedToUpnAO: null,
          assignedToDisplayName: null,
          assignedToDisplayNameAO: null,
          closed: false,
          status: null,
        }));
        dispatch({
          type: "ADD_ALL_ORGS",
          payload: values,
        });
      })
      .catch((err) => console.error(err));
  };

  return (
    <>
      <Box display="flex">
        <Box flexGrow={1} className={classes.title}>
          <h4>Awarding Organisations ({orgs.length})</h4>
        </Box>
        <Box>
          <Button
            color="primary"
            onClick={() => loadAllOrgs()}
            data-test="load-organisations-button"
          >
            Add all organisations
          </Button>{" "}
          <Button
            color="primary"
            onClick={() =>
              setDialogOpenProps({ isDialogOpen: true, initialSearchText: "" })
            }
            data-test="find-organisation-button"
          >
            Add an organisation
          </Button>
        </Box>
      </Box>
      <Paper className={classes.orgs} elevation={1}>
        <Box className={classes.header}>
          <Checkbox
            edge="start"
            checked={isEverythingToDelete}
            onChange={handleCheckAllChange}
            data-test="manage-recipients-select-all"
          />
          {isAnythingToDelete && (
            <Button color="primary" onClick={handleRemoveSelected}>
              Remove Selected
            </Button>
          )}
        </Box>
        <Divider />
        <List className={classes.body}>
          {orgs.map((org) => (
            <ManageRecipientOrg
              key={org.id}
              org={org}
              dispatch={dispatch}
              haveRoles={haveRoles}
            />
          ))}
        </List>
      </Paper>
      <SearchDialog
        label="Awarding Organisation(s)"
        service={mappedOrgLookupService}
        dialogOpenProps={dialogOpenProps}
        onSelectOrClose={handleSelectOrClose}
        selectedItems={orgs.map((org) => ({
          id: org.id,
        }))}
        datatest="find-ao-dialog"
        dialogWidth="md"
      />
    </>
  );
};
